import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { withEntityContext } from '@cdk-prod/fortellis-entity-context';
import { useOktaAuth } from '@okta/okta-react';
import axios from 'axios';
import { environmentURLs } from '../common/environment/CaptureEnvironment';
import {
  getOrGenerateIdentifierId,
  getOrgUnitId,
  resolveAnalyticsRoles
} from '../../utils/gtmUtils';

export function SolutionDetailsGtmDataLayer({
  solution,
  loading,
  auth,
  entityContext
}) {
  const okta = useOktaAuth();
  const authState =
    okta && 'authState' in okta
      ? okta.authState
      : auth?.isAuthenticated !== undefined
      ? { isAuthenticated: auth.isAuthenticated, isPending: false }
      : null;

  const location = useLocation();

  const lastPushedEvent = useRef(null);
  const [userRoles, setUserRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const userId = auth?.userData?.sub;
  const userEmail = auth?.userData?.email;
  const accessToken = auth?.accessToken;
  const isAuthenticated = authState?.isAuthenticated;
  const isAuthPending = authState?.isPending;

  const entity = entityContext?.entity;
  const entityId = entity?.id;
  const entityName = entity?.name;
  const entityOrgUnitId = entity?.orgUnitId;
  const entities = entityContext?.entities;
  const entitiesLoaded = entityContext?.entitiesLoaded;
  const loadingEntity = entityContext?.loadingEntity;

  const locationPathname = location?.pathname || '';
  const locationSearch = location?.search || '';

  // Fetch user profile roles when authenticated
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setUserRoles([]);
      setRolesLoaded(false);
      return;
    }

    let isMounted = true;
    const fetchUserRoles = async () => {
      try {
        const response = await axios.get(
          `${environmentURLs.accountsServerURL}/api/v1/users/profile/${userId}`,
          {
            headers: accessToken
              ? { Authorization: `Bearer ${accessToken}` }
              : {}
          }
        );
        if (isMounted) {
          const roles = response?.data?.profile?.roles;
          setUserRoles(Array.isArray(roles) ? roles : []);
          setRolesLoaded(true);
        }
      } catch (error) {
        if (isMounted) {
          console.error(
            'Failed to fetch user roles for Solution Details GTM data layer',
            error
          );
          setUserRoles([]);
          setRolesLoaded(true);
        }
      }
    };

    fetchUserRoles();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, userId, accessToken]);

  useEffect(() => {
    // Wait until Okta has finished determining authentication state
    if (!authState || isAuthPending) {
      return;
    }

    // If authenticated, wait for user data, roles, and organization context to load
    if (
      isAuthenticated &&
      (!userEmail || !rolesLoaded || !entitiesLoaded || loadingEntity)
    ) {
      return;
    }

    // Wait until solution data has finished loading
    if (loading) {
      return;
    }

    const currentUrl = locationPathname + locationSearch;

    let solutionsIdFromUrl = null;
    const uuidMatch = currentUrl.match(
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
    );
    solutionsIdFromUrl = uuidMatch ? uuidMatch[0] : null;

    const solutionsId = solution?.id || solutionsIdFromUrl || null;
    const solutionName = solution?.overview?.name || null;
    const solutionVersion =
      solution?.listingVersion != null ? String(solution.listingVersion) : null;

    const fortellisId = isAuthenticated
      ? entityId || auth?.entityId || null
      : null;
    const orgName = isAuthenticated ? entityName || null : null;
    const orgUnitId = isAuthenticated
      ? getOrgUnitId(entity, entities, fortellisId)
      : null;
    const storeId = orgUnitId || null;
    const storeName = orgName || null;
    const identifierId = getOrGenerateIdentifierId();
    const fortellisUserId = isAuthenticated
      ? auth?.userData?.sub || null
      : null;
    const normalizedUserRoles = resolveAnalyticsRoles(userRoles);
    const normalizedUserRolesKey = normalizedUserRoles
      ? normalizedUserRoles.join(',')
      : 'null';

    const eventKey = `${currentUrl}-${solutionsId || ''}-${solutionVersion ||
      ''}-${fortellisId || ''}-${fortellisUserId ||
      ''}-${normalizedUserRolesKey}`;

    if (lastPushedEvent.current !== eventKey) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'fortellis-pageview',
        page_section: 'Marketplace',
        page_subsection: 'Solutions',
        solutions_id: solutionsId,
        solution_name: solutionName,
        solution_version: solutionVersion,
        product: 'APP',
        application: 'Fortellis Network',
        identifier_id: identifierId,
        fortellis_user_id: fortellisUserId,
        user_id: null,
        enterprise_id: null,
        enterprise_name: null,
        company_id: null,
        store_id: storeId,
        store_name: storeName,
        stores: null,
        product_features: null,
        user_role: normalizedUserRoles,
        page_url: window.location.href,
        page_path: locationPathname,
        is_impersonated_session: false,
        fortellis_id: fortellisId,
        fortellis_org_name: orgName
      });

      lastPushedEvent.current = eventKey;
    }
  }, [
    loading,
    solution,
    locationPathname,
    locationSearch,
    auth,
    userEmail,
    userRoles,
    rolesLoaded,
    entity,
    entityId,
    entityName,
    entityOrgUnitId,
    entities,
    entitiesLoaded,
    loadingEntity,
    authState,
    isAuthPending,
    isAuthenticated
  ]);

  return null;
}

export default withAuth(withEntityContext(SolutionDetailsGtmDataLayer));
